import { useEffect, useState } from "react";
import { Check, Copy, Plus, Search, ShieldCheck, Shield, Trash2, UserPlus, X } from "lucide-react";
import { api } from "@/lib/api";

const STATUS_OPTS = ["Semua status", "Aktif", "Nonaktif"];
const TYPE_OPTS = ["Semua tipe", "Owner", "Member"];

const modSummary = (permissions, modules) =>
  permissions.includes("*") ? "Akses penuh ke semua modul" : permissions.map((p) => modules[p] || p).join(" · ");

function RoleChips({ roles }) {
  const shown = roles.slice(0, 3);
  return (
    <span className="role-chips">
      {shown.map((r) => <span key={r} className="role-chip">{r}</span>)}
      {roles.length > 3 && <span className="role-more">& {roles.length - 3} lainnya</span>}
    </span>
  );
}

function TempPasswordBox({ data, onClose }) {
  const copy = () => navigator.clipboard?.writeText(`Email: ${data.email}\nKata sandi sementara: ${data.temp_password}`);
  return (
    <div className="temp-pass-box" data-testid="temp-password-box">
      <ShieldCheck size={16} />
      <div>
        <b>Akun {data.email} siap digunakan.</b>
        <p>Kata sandi sementara: <code data-testid="temp-password-value">{data.temp_password}</code> — bagikan secara aman ke pengguna (tidak ditampilkan lagi).</p>
      </div>
      <button data-testid="temp-password-copy" className="secondary-btn small" onClick={copy}><Copy size={13} /> Salin</button>
      <button className="icon-btn" onClick={onClose}><X size={15} /></button>
    </div>
  );
}

function SettingsModal({ eyebrow, title, subtitle, testid, onClose, children, footer }) {
  return (
    <div className="modal-overlay" data-testid={testid} onClick={onClose}>
      <div className="modal-panel settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sm-head">
          <div>
            <p className="eyebrow">{eyebrow}</p>
            <h2>{title}</h2>
            {subtitle && <p className="muted sm-sub">{subtitle}</p>}
          </div>
          <button className="icon-btn" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="sm-body">{children}</div>
        <div className="sm-foot">{footer}</div>
      </div>
    </div>
  );
}

function UserModal({ initial, roles, modules, onClose, onSaved }) {
  const [form, setForm] = useState({ name: initial?.name || "", email: initial?.email || "", roles: initial?.roles || [] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const toggle = (r) => setForm((f) => ({ ...f, roles: f.roles.includes(r) ? f.roles.filter((x) => x !== r) : [...f.roles, r] }));
  const submit = async () => {
    setSaving(true); setError("");
    try {
      const r = initial
        ? await api(`/users/${initial.id}`, { method: "PATCH", body: JSON.stringify({ name: form.name, roles: form.roles }) })
        : await api("/users/invite", { method: "POST", body: JSON.stringify(form) });
      onSaved(r);
    } catch (e) { setError(e.message); }
    setSaving(false);
  };
  return (
    <SettingsModal eyebrow="PENGATURAN PENGGUNA" title={initial ? `Ubah ${initial.name}` : "Undang pengguna"}
      subtitle={initial ? "Perbarui nama atau peran akses pengguna ini." : "Sistem membuat kata sandi sementara yang bisa Anda salin dan bagikan sendiri — tanpa email otomatis."}
      testid="user-modal" onClose={onClose}
      footer={<>
        <button className="secondary-btn" onClick={onClose}>Tutup</button>
        <button data-testid="invite-save-button" className="primary-btn" disabled={!form.name.trim() || !form.email.trim() || form.roles.length === 0 || saving} onClick={submit}>{saving ? "Menyimpan..." : initial ? "Simpan Perubahan" : "Undang pengguna"}</button>
      </>}>
      {error && <div className="journal-balance bad" data-testid="user-modal-error">{error}</div>}
      <div className="tax-form-grid">
        <label>Nama lengkap<input data-testid="invite-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="mis. Sari Rahmawati" /></label>
        <label>Email<input data-testid="invite-email-input" type="email" value={form.email} disabled={Boolean(initial)} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="nama@perusahaan.co.id" /></label>
      </div>
      <p className="sm-field-label">Peran akses <span className="muted-inline">— bisa pilih lebih dari satu</span></p>
      <div className="role-pick-grid" data-testid="invite-roles">
        {roles.map((r) => {
          const on = form.roles.includes(r.name);
          return (
            <label key={r.name} className={`role-pick ${on ? "on" : ""}`}>
              <input data-testid={`invite-role-${r.name.toLowerCase().replaceAll(" ", "-")}`} type="checkbox" checked={on} onChange={() => toggle(r.name)} />
              <span className="pick-box">{on && <Check size={11} strokeWidth={3.5} />}</span>
              <span className="role-pick-body">
                <b>{r.name}{r.custom ? <em className="pick-custom">custom</em> : null}</b>
                <small>{modSummary(r.permissions, modules)}</small>
              </span>
            </label>
          );
        })}
      </div>
    </SettingsModal>
  );
}

function RoleModal({ modules, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", permissions: ["dashboard"] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const toggle = (m) => setForm((f) => ({ ...f, permissions: f.permissions.includes(m) ? f.permissions.filter((x) => x !== m) : [...f.permissions, m] }));
  const submit = async () => {
    setSaving(true); setError("");
    try { onSaved(await api("/roles", { method: "POST", body: JSON.stringify(form) })); }
    catch (e) { setError(e.message); }
    setSaving(false);
  };
  return (
    <SettingsModal eyebrow="PERAN CUSTOM" title="Tambah peran custom"
      subtitle="Tentukan modul apa saja yang bisa diakses pengguna dengan peran ini."
      testid="role-modal" onClose={onClose}
      footer={<>
        <button className="secondary-btn" onClick={onClose}>Tutup</button>
        <button data-testid="role-save-button" className="primary-btn" disabled={!form.name.trim() || form.permissions.length === 0 || saving} onClick={submit}>{saving ? "Menyimpan..." : "Simpan Peran"}</button>
      </>}>
      {error && <div className="journal-balance bad" data-testid="role-modal-error">{error}</div>}
      <div className="tax-form-grid one-col">
        <label>Nama peran<input data-testid="role-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="mis. Kasir Toko" /></label>
      </div>
      <p className="sm-field-label">Akses modul <span className="muted-inline">— {form.permissions.length} dipilih</span></p>
      <div className="mod-pick-grid" data-testid="role-permissions">
        {Object.entries(modules).map(([k, l]) => {
          const on = form.permissions.includes(k);
          return (
            <label key={k} className={`mod-pick ${on ? "on" : ""}`}>
              <input data-testid={`role-perm-${k}`} type="checkbox" checked={on} onChange={() => toggle(k)} />
              <span className="pick-box">{on && <Check size={11} strokeWidth={3.5} />}</span>
              {l}
            </label>
          );
        })}
      </div>
    </SettingsModal>
  );
}

export default function UsersSettings({ notify }) {
  const [users, setUsers] = useState([]);
  const [roleData, setRoleData] = useState({ modules: {}, roles: [] });
  const [tab, setTab] = useState("users");
  const [term, setTerm] = useState("");
  const [typeF, setTypeF] = useState("Semua tipe");
  const [statusF, setStatusF] = useState("Semua status");
  const [modal, setModal] = useState(null);
  const [roleModal, setRoleModal] = useState(false);
  const [tempPass, setTempPass] = useState(null);
  const [menuOpen, setMenuOpen] = useState(null);
  const load = () => {
    api("/users").then(setUsers).catch((e) => notify(e.message, "error"));
    api("/roles").then(setRoleData).catch(() => {});
  };
  useEffect(load, []); // eslint-disable-line
  const filtered = users
    .filter((u) => typeF === "Semua tipe" || u.type === typeF)
    .filter((u) => statusF === "Semua status" || (statusF === "Aktif") === (u.is_active !== false))
    .filter((u) => !term || u.name.toLowerCase().includes(term.toLowerCase()) || u.email.toLowerCase().includes(term.toLowerCase()));
  const usage = users.reduce((acc, u) => { (u.roles || []).forEach((r) => { acc[r] = (acc[r] || 0) + 1; }); return acc; }, {});
  const act = async (fn, okMsg) => {
    setMenuOpen(null);
    try { await fn(); if (okMsg) notify(okMsg); load(); } catch (e) { notify(e.message, "error"); }
  };
  return (
    <div data-testid="users-settings">
      <div className="users-toolbar">
        <div className="users-tabs">
          <button data-testid="users-tab-list" className={`contact-tab ${tab === "users" ? "active" : ""}`} onClick={() => setTab("users")}>Daftar pengguna</button>
          <button data-testid="users-tab-roles" className={`contact-tab ${tab === "roles" ? "active" : ""}`} onClick={() => setTab("roles")}>Peran &amp; hak akses</button>
        </div>
        <div className="toolbar-spacer" />
        <button data-testid="add-custom-role-button" className="secondary-btn nowrap" onClick={() => setRoleModal(true)}><Plus size={14} /> Tambah peran custom</button>
        <button data-testid="invite-user-button" className="primary-btn nowrap" onClick={() => setModal({})}><UserPlus size={14} /> Undang pengguna</button>
      </div>
      {tempPass && tab === "users" && <TempPasswordBox data={tempPass} onClose={() => setTempPass(null)} />}
      {tab === "users" && (
        <>
          <div className="workflow-toolbar surface prod-toolbar">
            <select data-testid="users-type-filter" value={typeF} onChange={(e) => setTypeF(e.target.value)}>{TYPE_OPTS.map((o) => <option key={o}>{o}</option>)}</select>
            <select data-testid="users-status-filter" value={statusF} onChange={(e) => setStatusF(e.target.value)}>{STATUS_OPTS.map((o) => <option key={o}>{o}</option>)}</select>
            <div className="search-box"><Search size={14} /><input data-testid="users-search" placeholder="Cari pengguna..." value={term} onChange={(e) => setTerm(e.target.value)} /></div>
            <span className="record-count">{filtered.length} pengguna</span>
          </div>
          <div className="surface workflow-table-wrap auto-h">
            <table className="workflow-table" data-testid="users-table">
              <thead><tr><th>NAMA PENGGUNA</th><th>PERAN</th><th>STATUS</th><th>TIPE</th><th className="right">TINDAKAN</th></tr></thead>
              <tbody>
                {filtered.map((u, i) => (
                  <tr key={u.id} data-testid={`user-row-${i}`}>
                    <td><div className="user-cell"><span className="user-avatar">{(u.name || "?").slice(0, 2).toUpperCase()}</span><span><strong>{u.name}</strong><small>{u.email}</small></span></div></td>
                    <td><RoleChips roles={u.roles} /></td>
                    <td><span className={`status ${u.is_active !== false ? "paid" : "void"}`}>{u.is_active !== false ? "Aktif" : "Nonaktif"}</span></td>
                    <td>{u.type}</td>
                    <td className="right users-action-cell">
                      {u.type !== "Owner" && (
                        <>
                          <button data-testid={`user-action-${i}`} className="secondary-btn small" onClick={() => setMenuOpen(menuOpen === u.id ? null : u.id)}>Tindakan ▾</button>
                          {menuOpen === u.id && (
                            <div className="cb-menu users-menu" data-testid={`user-menu-${i}`}>
                              <button data-testid={`user-edit-${i}`} onClick={() => { setMenuOpen(null); setModal(u); }}>Ubah peran</button>
                              <button data-testid={`user-reset-${i}`} onClick={() => act(async () => setTempPass({ email: u.email, ...(await api(`/users/${u.id}/reset-password`, { method: "POST" })) }))}>Reset kata sandi</button>
                              <button data-testid={`user-toggle-${i}`} onClick={() => act(() => api(`/users/${u.id}`, { method: "PATCH", body: JSON.stringify({ is_active: u.is_active === false }) }), u.is_active !== false ? `${u.name} dinonaktifkan` : `${u.name} diaktifkan`)}>{u.is_active !== false ? "Nonaktifkan" : "Aktifkan"}</button>
                              <button data-testid={`user-delete-${i}`} onClick={() => act(() => api(`/users/${u.id}`, { method: "DELETE" }), `${u.name} dihapus`)}>Hapus</button>
                            </div>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {tab === "roles" && (
        <div className="role-grid" data-testid="roles-table">
          {roleData.roles.map((r, i) => (
            <div key={r.name} className="surface role-card-item" data-testid={`role-row-${i}`}>
              <div className="role-card-top">
                <span className={`role-avatar ${r.custom ? "custom" : ""}`}>{r.custom ? <Shield size={15} /> : <ShieldCheck size={15} />}</span>
                <div className="role-card-title">
                  <strong>{r.name}</strong>
                  <small>{usage[r.name] || 0} pengguna memakai peran ini</small>
                </div>
                <span className={`role-kind ${r.custom ? "custom" : ""}`}>{r.custom ? "Custom" : "Bawaan"}</span>
              </div>
              <div className="role-mods">
                {r.permissions.includes("*")
                  ? <span className="mod-chip all">Semua modul</span>
                  : r.permissions.map((p) => <span key={p} className="mod-chip">{roleData.modules[p] || p}</span>)}
              </div>
              {r.custom && (
                <div className="role-card-foot">
                  <button data-testid={`role-delete-${i}`} className="danger-link" onClick={() => act(() => api(`/roles/${r.id}`, { method: "DELETE" }), `Peran ${r.name} dihapus`)}><Trash2 size={13} /> Hapus peran</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {modal && <UserModal initial={modal.id ? modal : null} roles={roleData.roles} modules={roleData.modules} onClose={() => setModal(null)}
        onSaved={(r) => { setModal(null); if (r.temp_password) setTempPass(r); notify(`Pengguna ${r.name || ""} disimpan`); load(); }} />}
      {roleModal && <RoleModal modules={roleData.modules} onClose={() => setRoleModal(false)} onSaved={(r) => { setRoleModal(false); notify(`Peran "${r.name}" dibuat`); load(); }} />}
    </div>
  );
}
